// Lanjutkan dengan Google. The two sign-in modes are INDEPENDENT and are shown
// side by side, because GET /auth/google/config reports them independently
// (`enabled` = an OAuth client is stored, `demo` = the simulated login is on):
//   enabled          "Lanjutkan dengan Google": a full-page navigation to the
//                    API's /auth/google/start (with a safe ?next=); Google
//                    returns to the app with the session cookie set.
//                    Not enabled -> the button is disabled and
//                    "Login Google belum dikonfigurasi admin." is shown.
//   demo             "Masuk mode simulasi": the dialog with Nama and Email,
//                    which calls POST /auth/google/demo.
// While the config loads, or when it cannot be read, the simulated button is
// kept (the demo endpoint itself answers 404 when it is off) so the page is
// never left with no way in.
import { useEffect, useState, type FormEvent } from "react";
import { getGuestState } from "../chat/guest-store";
import * as API from "../api";
import { useAuth } from "../auth";
import { navigate } from "../router";
import { Badge, Dialog, Field, fieldAria, Notice } from "../ui/components";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface GoogleErrors {
  name?: string;
  email?: string;
}

export function validateGoogle(name: string, email: string): GoogleErrors {
  const e: GoogleErrors = {};
  if (!name.trim()) e.name = "Nama wajib diisi.";
  else if (name.trim().length > 120) e.name = "Nama paling banyak 120 karakter.";
  if (!email.trim()) e.email = "Email wajib diisi.";
  else if (!EMAIL.test(email.trim())) e.email = "Format email belum benar, contoh nama@gmail.com.";
  return e;
}

/** What a 409 demo_unavailable says when the API sent no message of its own. */
export const DEMO_UNAVAILABLE_FALLBACK = "Masuk dengan Google tidak tersedia untuk email ini. Bila Anda memiliki akun dengan kata sandi, masuk dengan email dan kata sandi.";

/**
 * The one notice a failed simulated sign-in shows. A 409 demo_unavailable
 * shows the API's own message: it never says which emails exist.
 */
export function describeGoogleFailure(err: unknown): { title: string; body: string } {
  const status = err instanceof API.ApiError ? err.status : 0;
  if (status === 409) {
    const own = err instanceof API.ApiError && err.body && typeof err.body.error === "string" && err.body.error.trim() ? err.body.error.trim() : null;
    return { title: "Tidak dapat masuk dengan email ini", body: own ?? DEMO_UNAVAILABLE_FALLBACK };
  }
  if (status === 404) return { title: "Masuk dengan Google belum tersedia", body: "Fitur ini sedang dimatikan di server. Coba lagi nanti." };
  if (status === 429) return { title: "Terlalu banyak percobaan", body: "Tunggu beberapa saat, lalu coba lagi." };
  return { title: "Belum bisa masuk", body: API.errorMessage(err) };
}

export function GoogleSignInDialog({ open, onClose, next = null }: { open: boolean; onClose: () => void; next?: string | null }) {
  const { signIn } = useAuth();
  // The name a guest gave Pluto on this device, as a starting value.
  const [name, setName] = useState(() => getGuestState().profile?.name ?? "");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<GoogleErrors>({});
  const [failure, setFailure] = useState<{ title: string; body: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const found = validateGoogle(name, email);
    setErrors(found);
    setFailure(null);
    if (found.name || found.email) {
      document.getElementById(found.name ? "google-name" : "google-email")?.focus();
      return;
    }
    setBusy(true);
    try {
      const res = await API.googleDemoLogin(email.trim().toLowerCase(), name.trim());
      setBusy(false);
      onClose();
      signIn(res.user);
      // The real Google route returns to `next` itself; the simulated one opens it here.
      if (next) navigate(next, { replace: true });
    } catch (err) {
      setFailure(describeGoogleFailure(err));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      size="sm"
      title="Lanjutkan dengan Google"
      description="Belum memakai akun Google sungguhan. Isi nama dan email untuk membuat atau membuka akun demo."
      busy={busy}
      onClose={onClose}
    >
      <form className="google-form" noValidate aria-label="Mode simulasi" onSubmit={submit}>
        <p className="google-mode">
          <Badge tone="info">Mode simulasi</Badge>
        </p>
        {failure ? (
          <Notice tone="danger" title={failure.title}>
            <p>{failure.body}</p>
          </Notice>
        ) : null}
        <Field id="google-name" label="Nama" error={errors.name}>
          <input
            {...fieldAria("google-name", errors.name)}
            data-autofocus=""
            name="name"
            autoComplete="name"
            maxLength={120}
            value={name}
            readOnly={busy}
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </Field>
        <Field id="google-email" label="Email" error={errors.email}>
          <input
            {...fieldAria("google-email", errors.email)}
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="nama@gmail.com"
            value={email}
            readOnly={busy}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
        </Field>
        <button type="submit" className="btn btn-block" aria-busy={busy || undefined}>
          {busy ? "Membuka akun" : "Lanjutkan"}
        </button>
      </form>
    </Dialog>
  );
}

/** The full-page navigation, swappable in tests (happy-dom cannot leave the page). */
export const googleNav = {
  assign(url: string): void {
    window.location.assign(url);
  },
};

export const GOOGLE_NOT_CONFIGURED = "Login Google belum dikonfigurasi admin.";
export const GOOGLE_REAL_LABEL = "Lanjutkan dengan Google";
export const GOOGLE_DEMO_LABEL = "Masuk mode simulasi";

/** GET /auth/google/config, or null while loading or when it failed. */
export function useGoogleConfig(): API.GoogleConfig | null {
  const [config, setConfig] = useState<API.GoogleConfig | null>(null);
  useEffect(() => {
    let alive = true;
    API.getGoogleConfig().then(
      (c) => alive && setConfig(c),
      () => alive && setConfig(null),
    );
    return () => {
      alive = false;
    };
  }, []);
  return config;
}

/**
 * The two sign-in choices, in this order: the real Google account (a redirect,
 * or disabled with the not-configured note) and the simulated login (a dialog).
 * Each one appears only when the config reports its own mode, so both, one, or
 * — once the simulation is removed and no client is stored — a single disabled
 * button can be on screen.
 */
export function GoogleButton({ variant = "primary", block = false, next = null }: { variant?: "primary" | "secondary"; block?: boolean; next?: string | null }) {
  const [open, setOpen] = useState(false);
  const config = useGoogleConfig();
  const cls = (secondary: boolean) => ["btn", secondary || variant === "secondary" ? "btn-secondary" : "", block ? "btn-block" : ""].filter(Boolean).join(" ");
  // While the config is unknown, keep the simulated route only: it is the one
  // whose endpoint refuses on its own when it is off.
  const demo = config === null || config.demo;
  return (
    <>
      {config?.enabled ? (
        <button type="button" className={cls(false)} onClick={() => googleNav.assign(API.googleStartUrl({ next }))}>
          {GOOGLE_REAL_LABEL}
        </button>
      ) : config ? (
        <>
          <button type="button" className={cls(false)} disabled>
            {GOOGLE_REAL_LABEL}
          </button>
          <p className="login-help" role="status">
            {GOOGLE_NOT_CONFIGURED}
          </p>
        </>
      ) : null}
      {demo ? (
        <>
          <button type="button" className={cls(Boolean(config?.enabled))} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
            {GOOGLE_DEMO_LABEL}
          </button>
          <GoogleSignInDialog open={open} next={next} onClose={() => setOpen(false)} />
        </>
      ) : null}
    </>
  );
}
